import { useEffect, useRef, useState } from "react";
import User from "../../../models/User";
import AuthService from "../../../services/AuthService";
import Logger from "../../../utils/Logger";
import { useHistory } from "react-router";
import { HttpStatus } from "../../../utils/constants";
import useRefreshToken from "./useRefreshToken";
import { proxyStorage } from "../../../utils/proxyStorage";
import { resolveAssetUrl } from "../../../utils/assetUrl";
import { reloadAdminPermissions } from "../useAdminPermissions";

const Log = Logger.getLogger("useAuthentication");

export const LocalTokenKey = "token";
export const LocalUserIdKey = "user-id";
export const LocalUserInfoKey = "user-info";
export const LocalRefreshTokenKey = "refresh-token";
export const LocalTokenExpireKey = "token-expire";

export default function useAuthentication(
	props: {
		enableRefreshToken?: boolean;
	} | null
) {
	const [isValidated, setIsValidated] = useState(false);
	const [validating, setValidating] = useState(false);
	const [isFinishedValidate, setIsFinishedValidate] = useState(false);

	const [token, setToken] = useState(() => proxyStorage()?.getItem(LocalTokenKey) || null);
	const [userInfo, setUserInfo] = useState(() => loadLocalUserInfo());
	const authSessionEpochRef = useRef(0);
	const tokenRef = useRef<string | null>(token);
	const forceLogoutInFlightRef = useRef<Promise<void> | null>(null);

	useEffect(() => {
		tokenRef.current = token;
	}, [token]);

	const clearLocalAuthState = () => {
		localStorage.removeItem(LocalTokenKey);
		localStorage.removeItem(LocalUserIdKey);
		localStorage.removeItem(LocalUserInfoKey);
		localStorage.removeItem(LocalRefreshTokenKey);
		localStorage.removeItem(LocalTokenExpireKey);
		tokenRef.current = null;
		setToken(null);
		setUserInfo(null);
		setIsValidated(false);
	};

	const history = useHistory();

	const { handleLoginSuccess, onValidateFail, onValidateSuccess } = useRefreshToken({
		getSessionEpoch: () => authSessionEpochRef.current,
		onRefreshTokenSuccess: (nToken, nUserInfo) => {
			if (!nToken || !nUserInfo) return;

			authSessionEpochRef.current += 1;
			tokenRef.current = nToken;
			setToken(nToken);
			setUserInfo(nUserInfo);
			localStorage.setItem(LocalTokenKey, nToken || "");
			localStorage.setItem(LocalUserIdKey, nUserInfo.id + "");
			storeLocalUserInfo(nUserInfo);

			setIsValidated(true);
			setIsFinishedValidate(true);

			// Same stale-cache issue as onLoginSuccess: a permission fetch that 401'd
			// against the expired token must be replaced now that we have a fresh one.
			void reloadAdminPermissions();
		},
		onRefreshTokenFail: () => {
			clearLocalAuthState();
			AuthService.logout().catch(() => {});
			setIsFinishedValidate(true);
		},
	});

	/**
	 * validate current user still authenticated
	 */
	const validate = async (backUrl?: string | null) => {
		const snap = tokenRef.current;
		if (!snap) {
			setIsFinishedValidate(true);
			return;
		}

		setValidating(true);
		try {
			await AuthService.validate(snap);

			if (tokenRef.current !== snap) {
				return;
			}

			// Always fetch fresh user info from server to ensure role/status are up to date
			try {
				const data = await AuthService.getUserInfo(snap);
				if (tokenRef.current !== snap) {
					return;
				}
				const fullUser = new User().parse(data);
				fullUser.token = snap;
				setUserInfo(fullUser);
				storeLocalUserInfo(fullUser);
			} catch (userErr) {
				console.error("❌ Failed to load user info:", userErr);
			}

			if (tokenRef.current !== snap) {
				return;
			}

			setIsValidated(true);
			setIsFinishedValidate(true);

			//trigger refreshToken tracking job
			if (props?.enableRefreshToken) {
				onValidateSuccess();
			}
		} catch (err: any) {
			console.error(err);

			if (tokenRef.current !== snap) {
				return;
			}

			if (err?.response?.status == HttpStatus.Unauthorize || err?.response?.status == HttpStatus.Forbidden) {
				//trigger refreshToken
				if (props?.enableRefreshToken) {
					onValidateFail();
				} else {
					clearLocalAuthState();
					AuthService.logout().catch(() => {});
					setIsFinishedValidate(true);
				}
			} else {
				setIsFinishedValidate(true);
			}
		} finally {
			setValidating(false);
		}
	};

	const forceLogout = async (backUrl?: string | null, targetUrl?: string) => {
		if (forceLogoutInFlightRef.current) {
			await forceLogoutInFlightRef.current;
			return;
		}

		forceLogoutInFlightRef.current = (async () => {
			const epochWhenLogoutStarted = authSessionEpochRef.current;
			Log.log(`call force logout`, backUrl);
			if (targetUrl) {
				history.push(backUrl ? `/?back=${encodeURIComponent(backUrl)}` : `/`);
			}

			try {
				await AuthService.logout();
			} catch (_) {
				// Ignore - cookies may already be cleared
			}

			if (authSessionEpochRef.current !== epochWhenLogoutStarted) {
				Log.log(`forceLogout skipped (session superseded by login/refresh)`);
				return;
			}

			clearLocalAuthState();
		})();

		try {
			await forceLogoutInFlightRef.current;
		} finally {
			forceLogoutInFlightRef.current = null;
		}
	};

	const onLoginSuccess = (user: User, onSuccess?: () => void) => {
		if (!user) return;

		authSessionEpochRef.current += 1;
		const epochAtLogin = authSessionEpochRef.current;

		// Critical: login JSON often has no `role`. If we set token + isFinishedValidate while still
		// "not validated", PrivateRoute treats it as a broken session and calls forceLogout.
		// Also requireAdmin sees role !== Admin and forceLogout. Load /me first, then mark finished.
		setIsFinishedValidate(false);

		tokenRef.current = user.token || null;
		setToken(user.token);
		setUserInfo(user);

		localStorage.setItem(LocalTokenKey, user.token || "");
		localStorage.setItem(LocalUserIdKey, user.id + "");

		void (async () => {
			const t = user.token;
			let resolved = user;

			if (t) {
				try {
					const data = await AuthService.getUserInfo(t);
					if (authSessionEpochRef.current !== epochAtLogin) {
						return;
					}
					const fullUser = new User().parse(data);
					fullUser.token = t;
					fullUser.refreshToken = user.refreshToken ?? fullUser.refreshToken;
					fullUser.expireTime = user.expireTime ?? fullUser.expireTime;
					resolved = fullUser;
				} catch (e) {
					console.error("Failed to load user profile after login:", e);
					if (authSessionEpochRef.current !== epochAtLogin) {
						return;
					}
				}
			}

			if (authSessionEpochRef.current !== epochAtLogin) {
				return;
			}

			tokenRef.current = resolved.token || null;
			setUserInfo(resolved);
			storeLocalUserInfo(resolved);

			setIsValidated(true);
			setIsFinishedValidate(true);

			// The RBAC permission cache may hold a stale pre-login result (a 401'd
			// /auth/me is cached with loaded=true), which made every gated page show
			// "Access Denied" until a hard refresh. Re-fetch with the new session.
			void reloadAdminPermissions();

			if (props?.enableRefreshToken) {
				handleLoginSuccess(resolved);
			}
			onSuccess?.();
		})();
	};

	const updateUserInfo = (
		nAvatar: string | null,
		nFirstName: string | null,
		nLastName: string | null,
		phone: string | null
	) => {
		let userName = [nFirstName, nLastName].filter((val) => val).join(" ");

		let nUser = new User({
			...userInfo,
			...(nAvatar && { avatar: resolveAssetUrl(nAvatar) }),
			...((nFirstName || nFirstName === "") && { firstName: nFirstName }),
			...((nLastName || nLastName === "") && { fullName: nLastName }),
			...(phone && { phone: phone }),
		});
		setUserInfo(nUser);
		storeLocalUserInfo(nUser);
	};

	useEffect(() => {
		validate();
	}, []);

	return {
		isValidated,
		validating,
		isFinishedValidate,
		token,
		userInfo,
		validate,
		forceLogout,
		onLoginSuccess,
		updateUserInfo,
	};
}

export function loadLocalUserInfo(): User | null {
	if (!proxyStorage()?.getItem(LocalUserIdKey)) return null;
	if (!proxyStorage()?.getItem(LocalUserInfoKey)) return null;

	let uInfoRaw: any = null;
	let uId: string = "";
	try {
		uId = proxyStorage()?.getItem(LocalUserIdKey) || "";
		uInfoRaw = JSON.parse(proxyStorage()?.getItem(LocalUserInfoKey) || "");
	} catch (err) {
		console.error(err);
	}

	if (!uId || !uInfoRaw) return null;

	const user = new User({
		...uInfoRaw,
		id: proxyStorage()?.getItem(LocalUserIdKey),
	});

	if (user.role === null || user.role === undefined) {
		localStorage.removeItem(LocalUserInfoKey);
		localStorage.removeItem(LocalTokenKey);
		localStorage.removeItem(LocalUserIdKey);
		return null;
	}

	return user;
}

export function storeLocalUserInfo(userInfo: User) {
	if (!userInfo) return;

	// Security: only store non-sensitive fields needed for UI display & auth checks
	const dataToStore = {
		role: userInfo.role,
		urlImage: userInfo.avatar,
		userName: userInfo.fullName,
		fullName: userInfo.fullName,
		displayName: userInfo.displayName,
		active: userInfo.active,
		partnerId: userInfo.partnerId,
	};

	localStorage.setItem(LocalUserInfoKey, JSON.stringify(dataToStore));
}
